import React from 'react';
import { Icon, type IconProps } from '../Icon/Icon';
import { useIconButtonVariant } from './useIconVariant';

type Navigation = 'right' | 'left' | 'close';
export type Variant = 'play' | 'favorite-logo' | 'favorite-logo-no-bg' | 'favorite-animation' | 'seat' | Navigation;

type SizeLimiter<V> = V extends Navigation
  ? never
  : V extends 'seat'
  ? Exclude<IconProps['size'], 'medium'>
  : IconProps['size'];

type HasHideBgProp<V> = V extends 'favorite-animation' | Navigation ? boolean : never;
type HasCheckedProp<V> = V extends 'favorite-animation' ? boolean : never;
type HasDisabledProp<V> = V extends 'seat' ? boolean : never;

export interface IconButtonProps<V> extends Omit<IconProps, 'icon' | 'size'> {
  variant: V;
  size: SizeLimiter<V>;
  checked?: HasCheckedProp<V>;
  hideBackground?: HasHideBgProp<V>;
  disabled?: HasDisabledProp<V>;
}

export interface IconButtonWithHref extends IconButtonProps<Variant> {
  href: string;
  action?: never;
}

export interface IconButtonWithOnAction extends IconButtonProps<Variant> {
  href?: never;
  action: () => void;
}

export const IconButton = ({
  variant,
  action,
  href,
  size,
  ariaLabel,
  role,
  hidden,
  hideBackground,
  checked,
  disabled,
  tabIndex,
  as = 'button',
  className = '',
}: IconButtonWithHref | IconButtonWithOnAction) => {
  const { icon, style, iconSize, iconPadding } = useIconButtonVariant({
    variant,
    size,
    hideBackground,
    checked,
    disabled,
  });

  const props = {
    icon,
    size: iconSize,
    ariaLabel,
    role,
    hidden,
    href,
    disabled,
    as,
  };

  return (
    <Icon
      className={`rounded-full border-0 focus-visible:ring-primary ${iconPadding} ${style} ${className}`}
      onClick={action}
      tabIndex={tabIndex}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          action?.();
        }
      }}
      {...props}
    />
  );
};
